import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../services/adminService';
import { 
  BarChart3, Calendar, Download, TrendingUp, TrendingDown,
  Users, CheckCircle, Package, DollarSign, RefreshCw
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SalesReport = () => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showAllTelecallers, setShowAllTelecallers] = useState(false);

  const { data: salesData, isLoading, refetch } = useQuery({
    queryKey: ['salesReport', dateRange],
    queryFn: async () => {
      const startDateStr = dateRange.startDate.toISOString().split('T')[0];
      const endDateStr = dateRange.endDate.toISOString().split('T')[0];

      // Get leads count
      const { count: leadsCount } = await supabase
        .from('leads')
        .select('id', { count: 'exact' })
        .gte('created_at', `${startDateStr} 00:00:00`)
        .lte('created_at', `${endDateStr} 23:59:59`);

      // Get converted leads
      const { count: convertedCount } = await supabase
        .from('leads')
        .select('id', { count: 'exact' })
        .eq('status', 'converted')
        .gte('created_at', `${startDateStr} 00:00:00`)
        .lte('created_at', `${endDateStr} 23:59:59`);

      // Get delivered orders revenue
      const { data: deliveredOrders } = await supabase
        .from('master_orders')
        .select('final_amount')
        .in('current_status_type', ['delivered', 'DELIVERED'])
        .gte('sr_status_raw_time', `${startDateStr} 00:00:00`)
        .lte('sr_status_raw_time', `${endDateStr} 23:59:59`);

      const totalRevenue = deliveredOrders?.reduce((sum, order) => sum + (Number(order.final_amount) || 0), 0) || 0;
      const deliveredLeads = deliveredOrders?.length || 0;

      const totalLeads = leadsCount || 0;
      const convertedLeads = convertedCount || 0;
      const conversionRate = totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0;
      const deliveryRate = convertedLeads > 0 ? (deliveredLeads / convertedLeads) * 100 : 0;

      // Get top team leads (simplified)
      const { data: tlData } = await supabase
        .from('users')
        .select('id, name')
        .limit(10);

      const topTeamLeads = (tlData || []).map(tl => ({
        id: tl.id,
        name: tl.name,
        assigned: 0,
        converted: 0,
        delivered: 0,
        telecallers: 0,
        revenue: 0
      }));

      // Get top telecallers (simplified)
      const topTelecallers = [];

      return {
        totalLeads,
        convertedLeads,
        deliveredLeads,
        totalRevenue,
        conversionRate,
        deliveryRate,
        topTeamLeads,
        topTelecallers,
        salesTrends: [],
        conversionFunnel: [
          { stage: 'Leads', count: totalLeads, percentage: 100, color: '#667eea' },
          { stage: 'Converted', count: convertedLeads, percentage: conversionRate, color: '#f59e0b' },
          { stage: 'Delivered', count: deliveredLeads, percentage: deliveryRate, color: '#10b981' }
        ]
      };
    }
  });

  const formatCurrency = (value) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
  const formatDate = (date) => date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const COLORS = ['#667eea', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sales data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Reports</h1>
          <p className="text-gray-600 mt-1">Comprehensive sales analytics and reporting</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Calendar size={20} />
            <span>Select Date Range</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download size={20} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      {showDatePicker && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate.toISOString().split('T')[0]}
                onChange={(e) => setDateRange({ ...dateRange, startDate: new Date(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={dateRange.endDate.toISOString().split('T')[0]}
                onChange={(e) => setDateRange({ ...dateRange, endDate: new Date(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Total Leads</span>
            <Users size={20} className="text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{salesData?.totalLeads || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Converted Leads</span>
            <CheckCircle size={20} className="text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{salesData?.convertedLeads || 0}</p>
          <p className="text-sm text-gray-500 mt-1">
            {salesData?.conversionRate?.toFixed(1) || 0}% conversion rate
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Delivered Leads</span>
            <Package size={20} className="text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{salesData?.deliveredLeads || 0}</p>
          <p className="text-sm text-gray-500 mt-1">
            {salesData?.deliveryRate?.toFixed(1) || 0}% delivery rate
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Total Revenue</span>
            <DollarSign size={20} className="text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{formatCurrency(salesData?.totalRevenue || 0)}</p>
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Conversion Funnel</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {salesData?.conversionFunnel?.map((stage, idx) => (
            <div key={idx} className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                <span className="text-sm font-semibold" style={{ color: stage.color }}>
                  {stage.count} ({stage.percentage.toFixed(1)}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="h-3 rounded-full transition-all"
                  style={{ width: `${stage.percentage}%`, backgroundColor: stage.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Funnel Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Conversion Breakdown</h2>
          {salesData?.conversionFunnel && (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={salesData.conversionFunnel}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ stage, percentage }) => `${stage}: ${percentage.toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {salesData.conversionFunnel.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Performance Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Overview</h2>
          {salesData?.conversionFunnel && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salesData.conversionFunnel}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="stage" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#667eea" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top Performers */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Top Team Leads</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Converted</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivered</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {salesData?.topTeamLeads?.slice(0, 10).map((tl) => (
                <tr key={tl.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{tl.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{tl.assigned}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{tl.converted}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{tl.delivered}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(tl.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesReport;
